// WHETHER THIS PERSON MAY BE SHOWN THE APP AT ALL. Not authored surface: the box restores this file
// from the template on every sync, and `isAgentAppWritable` refuses a write under `src/lib/platform/`.
//
// It is a PURE function of two values and imports nothing at run time, which is deliberate twice
// over: it is the whole of the gate, so it has to be testable without react, without a DOM and
// without this app's own node_modules — and a decision about who may see a product must never be
// entangled with the thing that renders it.
//
// WHAT IT IS FOR, measured rather than imagined. In an app where every person the design named is
// `serving` — a role the OWNER grants, never one taken at sign-up — a fresh sign-up holds
// better-auth's plain `user` and no app role. Every screen is role-gated, so their landing read is
// refused; `enterAfterDeniedRead` sends them to `/`; the app's own root guard resolves the entry table
// for somebody holding nothing and sends them back to the screen that just refused them; and the page
// renders the refusal. Every layer behaved exactly as designed, and the person was told nothing true.
// There is no route the app could send them to instead, because in such an app there is no screen
// they may see — so this is a STATE the landing renders, never another address.

import type { SessionInfo } from './auth';

/**
 * The generated access fact — `src/lib/gql/__generated__/app-access.ts`, projected from this app's own
 * contract by the box. `roles` is the same list the SERVER bakes as `APP_ROLES` (one authority,
 * `appRoles`), and `hasServedRole` says whether the design declared anybody who may act with no grant.
 */
export interface AppAccess {
  readonly roles: readonly string[];
  readonly hasServedRole: boolean;
}

/** The role every app's platform principal holds — the preview owner and the seeder — and the one
 *  slug that reads every role view in every app whatever that app's own role list says. */
const PLATFORM_ROLE = 'admin';

/** What the entry file should put on screen. `wait` renders nothing: the session has not landed. */
export type AccessGateVerdict = 'wait' | 'pending' | 'app';

/**
 * THE WHOLE GATE, and every clause in it is load-bearing:
 *
 * `access === undefined` — this model has no auth. The fact is ABSENT rather than false, exactly as
 * the server emits no `roles.ts` for such a model, and it is answered FIRST so an app with no accounts
 * never reaches the session at all and gains no `get-session` probe it never had.
 *
 * `status === 'unknown'` — the session is published once, at the end of boot, and the first render
 * precedes it. Mounting the app under an unidentified visitor starts every feature machine against
 * the wrong person; flashing this screen at one who turns out to hold a role is the same lie
 * reversed. `unavailable` is NOT pending: a backend that never answered is an error state and says
 * nothing whatever about anyone's access.
 *
 * `roles.length > 0` — an app can honestly grant nobody anything: a cast the design declared no kind
 * for mints no role at all. Without this clause "holds none of them" is vacuously true for everybody
 * alive, including the builder previewing their own product. (It used to be reachable a second,
 * dishonest way — `grantableRoleSlug` answered undefined SILENTLY for a reserved name, so a design
 * whose only operator was called "Admin" had serving people and an EMPTY role list, and this clause
 * opened the staffed app to every session. The mint refuses that model now, at the derivation.)
 *
 * `hasServedRole` — a served person holds their capability by signing up. In an app that declares one,
 * somebody carrying no slug is a customer with screens of their own, not a person waiting on a grant.
 */
export function accessGateVerdict(access: AppAccess | undefined, session: SessionInfo): AccessGateVerdict {
  if (access === undefined) return 'app';
  if (session.status === 'unknown') return 'wait';
  if (session.status !== 'authed') return 'app';
  if (access.roles.length === 0 || access.hasServedRole) return 'app';
  const held = new Set(session.user?.roles ?? []);
  if (held.has(PLATFORM_ROLE)) return 'app';
  return access.roles.some((role) => held.has(role)) ? 'app' : 'pending';
}
